import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { formatCycleDate, resolveCycleEnd, cycleDurationDays, toDateInputValue } from '../../utils/cycleUtils';
import { Plus, Trash2, Pencil, Link2 } from 'lucide-react';
import MastersBackHeader from '../common/MastersBackHeader';

const emptyForm = { name: '', startDate: '', endDate: '', label: '', program_ids: [], start_video_url: '' };

/** Ciclos propios del gimnasio virtual (independientes de D28D plataforma). */
export default function GymMastersCycles({ onBack, scopeGymId = null }) {
  const { t } = useI18n();
  const [cycles, setCycles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchCycles = useCallback(async () => {
    if (!scopeGymId) {
      setCycles([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/cycles', { params: { gym_id: scopeGymId, gym_only: 'true' } });
      setCycles(res.data?.data || []);
    } catch {
      setError(t('gym.cycles.error_loading', 'Error cargando ciclos'));
    } finally {
      setLoading(false);
    }
  }, [scopeGymId, t]);

  const fetchPrograms = useCallback(async () => {
    if (!scopeGymId) {
      setPrograms([]);
      return;
    }
    try {
      const res = await api.get('/programs', { params: { gym_id: scopeGymId, gym_only: 'true' } });
      setPrograms(res.data?.data || []);
    } catch {
      setPrograms([]);
    }
  }, [scopeGymId]);

  useEffect(() => {
    fetchCycles();
    fetchPrograms();
  }, [fetchCycles, fetchPrograms]);

  const programNameById = useMemo(
    () => Object.fromEntries(programs.map((p) => [String(p.id), p.name])),
    [programs],
  );

  const programsForCycleRow = useCallback((cycle) => {
    if (cycle.program_ids?.length) {
      return programs.filter(
        (p) => cycle.program_ids.includes(String(p.id)) || cycle.program_ids.includes(p.id),
      );
    }
    return programs.filter((p) => Number(p.active_cycle_id) === Number(cycle.id));
  }, [programs]);

  const durationDays = useMemo(
    () => cycleDurationDays(form.startDate, form.endDate),
    [form.startDate, form.endDate],
  );

  const sortedCyclesByStart = useMemo(
    () => [...cycles].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    [cycles],
  );

  const resolvePreviousCycleVideoUrl = useCallback(() => {
    if (!sortedCyclesByStart.length) return '';
    if (editingId) {
      const idx = sortedCyclesByStart.findIndex((c) => Number(c.id) === Number(editingId));
      if (idx > 0) return sortedCyclesByStart[idx - 1].start_video_url || '';
      return '';
    }
    if (form.startDate) {
      const prev = sortedCyclesByStart.filter((c) => String(c.startDate) < form.startDate).pop();
      return prev?.start_video_url || '';
    }
    return sortedCyclesByStart[sortedCyclesByStart.length - 1]?.start_video_url || '';
  }, [sortedCyclesByStart, editingId, form.startDate]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (cycle) => {
    const linked = cycle.program_ids?.length
      ? [...cycle.program_ids]
      : programs.filter((p) => Number(p.active_cycle_id) === Number(cycle.id)).map((p) => p.id);
    setEditingId(cycle.id);
    setForm({
      name: cycle.name || '',
      startDate: toDateInputValue(cycle.startDate),
      endDate: toDateInputValue(cycle.endDate || cycle.end_date),
      label: cycle.label || '',
      program_ids: linked,
      start_video_url: cycle.start_video_url || '',
    });
    setShowForm(true);
  };

  const toggleProgram = (programId) => {
    setForm((prev) => {
      const ids = prev.program_ids || [];
      const has = ids.some((id) => String(id) === String(programId));
      return {
        ...prev,
        program_ids: has
          ? ids.filter((id) => String(id) !== String(programId))
          : [...ids, programId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.endDate) {
      setError(t('gym.cycles.end_required', 'Indica la fecha de fin del ciclo.'));
      return;
    }
    if (form.endDate < form.startDate) {
      setError(t('gym.cycles.end_before_start', 'La fecha de fin debe ser igual o posterior al inicio.'));
      return;
    }
    try {
      const payload = {
        name: form.name,
        startDate: form.startDate,
        endDate: form.endDate,
        label: form.label,
        gym_id: scopeGymId,
        program_ids: form.program_ids || [],
        start_video_url: form.start_video_url?.trim() || null,
      };
      if (editingId) {
        await api.put(`/cycles/${editingId}`, payload);
        setMessage(t('gym.cycles.updated', 'Ciclo actualizado'));
      } else {
        await api.post('/cycles', payload);
        setMessage(t('gym.cycles.created', 'Ciclo creado'));
      }
      resetForm();
      await fetchCycles();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError(t('gym.cycles.session_expired', 'Tu sesión expiró. Vuelve a iniciar sesión e intenta de nuevo.'));
      } else if (status === 403) {
        setError(err.response?.data?.error || t('gym.cycles.forbidden', 'No tienes permiso para guardar ciclos en esta sede.'));
      } else {
        setError(err.response?.data?.error || t('gym.cycles.save_error', 'Error guardando ciclo'));
      }
    }
  };

  const handleDelete = async (cycle) => {
    if (!window.confirm(t('gym.cycles.delete_confirm', '¿Borrar este ciclo?'))) return;
    try {
      await api.delete(`/cycles/${cycle.id}`);
      setMessage(t('gym.cycles.deleted', 'Ciclo eliminado'));
      await Promise.all([fetchCycles(), fetchPrograms()]);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError(t('gym.cycles.session_expired', 'Tu sesión expiró. Vuelve a iniciar sesión e intenta de nuevo.'));
      } else if (status === 403) {
        setError(err.response?.data?.error || t('gym.cycles.forbidden', 'No tienes permiso para borrar este ciclo.'));
      } else {
        setError(err.response?.data?.error || t('gym.cycles.delete_error', 'No se pudo borrar el ciclo'));
      }
    }
  };

  return (
    <div className="dashboard-main-view surface-readable d28d-admin-surface space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1">
          {onBack ? (
            <MastersBackHeader onBack={onBack} title={t('gym.masters.cycles', 'Ciclos de tu gimnasio')} />
          ) : (
            <>
              <h2 className="text-xl font-bold text-stone-900">{t('gym.masters.cycles', 'Ciclos de tu gimnasio')}</h2>
              <p className="text-sm text-stone-600 mt-1">
                {t(
                  'gym.masters.cycles_desc_linked',
                  'Define el periodo de cada ciclo con fecha de inicio y fin, vincula los programas de tu sede y asígnalos en Planes.',
                )}
              </p>
            </>
          )}
          {onBack ? (
            <p className="text-sm text-stone-600 mt-1">
              {t(
                'gym.masters.cycles_desc_linked',
                'Define el periodo de cada ciclo con fecha de inicio y fin, vincula los programas de tu sede y asígnalos en Planes.',
              )}
            </p>
          ) : null}
        </div>
        {!showForm && (
          <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={() => { resetForm(); setShowForm(true); }}>
            <Plus className="w-4 h-4" />
            {t('gym.cycles.new', 'Nuevo ciclo')}
          </button>
        )}
      </header>

      {programs.length === 0 && !loading ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {t('gym.cycles.need_programs', 'Crea programas en Maestros → Programas para vincularlos a cada ciclo.')}
        </div>
      ) : null}

      {error ? <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">{error}</div> : null}
      {message ? <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-sm">{message}</div> : null}

      {showForm && (
        <form onSubmit={handleSubmit} className="ui-light-panel border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.cycles.name', 'Nombre')}</span>
              <input className="ui-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ciclo 1" required />
            </label>
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.cycles.start', 'Fecha de inicio')}</span>
              <input className="ui-input mt-1" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
            </label>
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.cycles.end', 'Fecha de fin')}</span>
              <input
                className="ui-input mt-1"
                type="date"
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                required
              />
            </label>
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.cycles.label', 'Etiqueta (opcional)')}</span>
              <input className="ui-input mt-1" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Especial, verano…" />
            </label>
          </div>
          {form.startDate && form.endDate && durationDays ? (
            <p className="text-xs text-stone-600">
              {t('gym.cycles.duration', 'Duración')}: {durationDays} {t('gym.cycles.days', 'días')}.
            </p>
          ) : null}

          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <span className="text-sm font-semibold text-stone-800">
                {t('gym.cycles.start_video', 'Video de inicio de ciclo')}
              </span>
              <button
                type="button"
                className="btn-secondary text-xs inline-flex items-center gap-1"
                disabled={!resolvePreviousCycleVideoUrl()}
                onClick={() => setForm((prev) => ({
                  ...prev,
                  start_video_url: resolvePreviousCycleVideoUrl(),
                }))}
              >
                <Link2 className="w-3.5 h-3.5" />
                {t('gym.cycles.copy_prev_video', 'Copiar del ciclo anterior')}
              </button>
            </div>
            <input
              className="ui-input"
              value={form.start_video_url || ''}
              onChange={(e) => setForm({ ...form, start_video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p className="text-xs text-stone-500">
              {t('gym.cycles.start_video_hint', 'Visible en Inicio para los participantes de este ciclo.')}
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-stone-800 inline-flex items-center gap-1.5">
                <Link2 className="w-4 h-4" />
                {t('gym.cycles.programs_in_cycle', 'Programas en este ciclo')}
              </span>
              {programs.length > 0 ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => setForm((prev) => ({ ...prev, program_ids: programs.map((p) => p.id) }))}
                  >
                    {t('gym.cycles.select_all_programs', 'Seleccionar todos')}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => setForm((prev) => ({ ...prev, program_ids: [] }))}
                  >
                    {t('gym.cycles.clear_programs', 'Quitar todos')}
                  </button>
                </div>
              ) : null}
            </div>
            {programs.length === 0 ? (
              <p className="text-sm text-stone-500">{t('gym.cycles.no_programs_yet', 'Sin programas en tu sede.')}</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {programs.map((p) => (
                  <label
                    key={p.id}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-stone-50 px-3 py-2 text-sm text-stone-800 cursor-pointer hover:bg-stone-100"
                  >
                    <input
                      type="checkbox"
                      checked={(form.program_ids || []).some((id) => String(id) === String(p.id))}
                      onChange={() => toggleProgram(p.id)}
                    />
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#cbd5e1' }} />
                    <span className="font-medium truncate">{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary">{editingId ? t('common.save', 'Guardar') : t('common.create', 'Crear')}</button>
            <button type="button" className="btn-secondary" onClick={resetForm}>{t('common.cancel', 'Cancelar')}</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-stone-600">{t('common.loading', 'Cargando…')}</p>
      ) : cycles.length === 0 ? (
        <p className="text-sm text-stone-600">{t('gym.cycles.none', 'Aún no hay ciclos. Crea el primero para tu gimnasio.')}</p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.cycles.name', 'Nombre')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.cycles.start', 'Inicio')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.cycles.end', 'Fin')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('common.program', 'Programas')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.cycles.label', 'Etiqueta')}</th>
                <th className="px-4 py-3 text-right font-semibold text-stone-600">{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((c) => {
                const linked = programsForCycleRow(c);
                return (
                  <tr key={c.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-medium text-stone-900">{c.name}</td>
                    <td className="px-4 py-3 text-stone-700">{formatCycleDate(c.startDate)}</td>
                    <td className="px-4 py-3 text-stone-700">{formatCycleDate(resolveCycleEnd(c))}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {linked.length
                        ? linked.map((p) => p.name).join(', ')
                        : (c.program_ids?.length
                          ? c.program_ids.map((id) => programNameById[String(id)] || id).join(', ')
                          : '—')}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{c.label || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button type="button" className="btn-secondary text-xs" onClick={() => startEdit(c)}>
                          <Pencil className="w-3 h-3 inline" /> {t('common.edit', 'Editar')}
                        </button>
                        <button type="button" className="btn-secondary text-xs text-red-700 border-red-200" onClick={() => handleDelete(c)}>
                          <Trash2 className="w-3 h-3 inline" /> {t('common.delete', 'Borrar')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
